import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProject, deleteProject, fetchProjects } from "../../store/projectSlice";
import { useNavigate } from "react-router-dom";

import { Formik } from "formik";
import * as Yup from "yup";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";

import "./Projects.css";

const schema = Yup.object({
  name: Yup.string().min(2, "Too short").max(80, "Too long").required("Project name required"),
  description: Yup.string().max(300, "Max 300 chars"),
});

export default function Projects() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error } = useSelector((s) => s.projects);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  const activeCount = useMemo(() => items.filter((p) => p.isActive).length, [items]);

  return (
    <div className="projectsPage">
      {/* HERO */}
      <div className="pHero">
        <div className="pHeroTop">
          <div style={{ minWidth: 0 }}>
            <div className="pTitle">Projects</div>
            <div className="pSub">Select a project to manage transactions & statements.</div>

            <div className="pStats">
              <div className="pPill">
                <FolderOpenOutlinedIcon style={{ fontSize: 18 }} />
                Total: {items.length}
              </div>
              <div className="pPill pPill--active">Active: {activeCount}</div>
            </div>
          </div>

          <div className="pHeroActions">
            <button className="pPrimaryBtn" onClick={() => setOpen(true)}>
              <AddIcon />
              New Project
            </button>
          </div>
        </div>
      </div>

      <div className="pDivider" />

      {/* STATES */}
      {loading && (
        <div className="pCenter">
          <CircularProgress />
        </div>
      )}

      {!loading && error && <div className="pError">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="pEmpty">
          <h3>No projects yet</h3>
          <p>Create your first project (example: HB Height) to start adding transactions.</p>
          <button className="pPrimaryBtn" onClick={() => setOpen(true)} style={{ marginTop: 12 }}>
            <AddIcon />
            Create Project
          </button>
        </div>
      )}

      {/* GRID */}
      {!loading && !error && items.length > 0 && (
        <div className="pGrid">
          {items.map((p) => (
            <div
              key={p._id}
              className="pCard"
              onClick={() => navigate(`/projects/${p._id}/transactions`)}
              role="button"
              tabIndex={0}
            >
              <div className="pCardBody">
                <div className="pCardTop">
                  <div className="pBadge">
                    {String(p.name || "P").slice(0, 1).toUpperCase()}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div className="pName">{p.name}</div>
                    <div className="pDesc">{p.description || "No description added"}</div>
                  </div>

                  <div className={`pStatus ${p.isActive ? "pStatus--active" : ""}`}>
                    {p.isActive ? "Active" : "Inactive"}
                  </div>
                </div>

                <div className="pMetaRow">
                  <div className="pChip">Transactions</div>
                  <div className="pChip">Statements</div>
                </div>

                <div className="pActions">
                  <button
                    className="pOpenBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${p._id}/transactions`);
                    }}
                  >
                    Open
                    <ArrowForwardIcon style={{ fontSize: 18 }} />
                  </button>

                  <button
                    className="pDelBtn"
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete project "${p.name}"?`)) {
                        dispatch(deleteProject(p._id));
                      }
                    }}
                  >
                    <DeleteOutlineIcon />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Project Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Create Project</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{ name: "", description: "" }}
            validationSchema={schema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              const res = await dispatch(createProject(values));
              setSubmitting(false);
              if (!res.error) {
                resetForm();
                setOpen(false);
              }
            }}
          >
            {({ values, handleChange, handleSubmit, touched, errors, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Project Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  margin="normal"
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                />

                <TextField
                  fullWidth
                  label="Description (optional)"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={3}
                  error={touched.description && !!errors.description}
                  helperText={touched.description && errors.description}
                />

                <button
                  type="submit"
                  className="pPrimaryBtn"
                  disabled={isSubmitting}
                  style={{ width: "100%", marginTop: 14, justifyContent: "center", opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? "Creating..." : "Create Project"}
                </button>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
